import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Table } from "reactstrap";
import { FormGroup, Row, Col, Input, Label } from "reactstrap";
import Select from "react-select";
import funcionogramaService from "services/FuncionogramaService";
import Swal from "sweetalert2";
import DeleteModal from "components/Common/DeleteModal";
import { useNavigate } from "react-router-dom";
import AddCargoModal from "./AddCargoModal";
import {
	FloppyDisk,
	Plus,
	Printer,
	SignOut,
	Trash,
} from "@phosphor-icons/react";
import { IconButton, Tooltip } from "@mui/material";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const columns = [
	{ name: "Nº", style: { width: 75 } },
	{ name: "Atividade", style: { width: "auto" } },
	{ name: "Ações", style: { width: 75 } },
];

const Functionogram = ({ onCloseClick, data }) => {
	const id = data?.id;
	const [atividadeIdDelete, setAtividadeIdDelete] = useState();
	const [showModalDeleteAtividade, setShowModalDeleteAtividade] =
		useState(false);
	const [showModalDelete, setShowModalDelete] = useState(false);
	const [cargos, setCargos] = useState([]);
	const [selectedCargo, setSelectedCargo] = useState();
	const [selectedCargoName, setSelectedCargoName] = useState("");
	const [atividades, setAtividades] = useState([]);
	const [novaAtividade, setNovaAtividade] = useState("");
	const [showModalAddCargo, setShowModalAddCargo] = useState(false);

	const navigate = useNavigate();

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	// useEffect para chamar getCargos apenas se o id existir
	useEffect(() => {
		if (id) {
			getCargos(id); // Chama a função apenas se o id estiver definido
		}
	}, [id]); // Executa o efeito sempre que o id mudar

	async function getCargos() {
		try {
			const response = await funcionogramaService.get(id);
			const cargosInfo = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));
			setCargos(cargosInfo);
		} catch (error) {
			console.log(error);
		}
	}

	useEffect(() => {
		if (selectedCargo?.value) getAtividades(selectedCargo?.value);
	}, [selectedCargo]);

	async function getAtividades(cargoId) {
		try {
			const response = await funcionogramaService.getAtividades(cargoId);
			const atividadesList = response.map((item) => ({
				id: item.id,
				value: item.name,
				isNew: false,
			}));
			setAtividades(atividadesList);
		} catch (error) {
			console.log(error);
		}
	}

	const handleChangeAtividade = (atividadeId, value) => {
		setAtividades((prev) =>
			prev.map((atividade) =>
				atividade.id === atividadeId
					? { ...atividade, value }
					: atividade,
			),
		);
	};

	const handleAddAtividade = () => {
		if (novaAtividade.trim()) {
			setAtividades((prev) => [
				...prev,
				{
					id: `new-${atividades.length + 1}`,
					value: novaAtividade,
					isNew: true,
				},
			]);
			setNovaAtividade("");
		} else {
			SendaCustomSwal.fire({
				iconHtml: '<i class="warning-icon"></i>',
				title: "Atenção",
				text: "A nova atividade não pode ser vazia!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	const handleChangeCargo = (field) => {
		setSelectedCargo(field);
		setSelectedCargoName(field.label);
	};

	const handleAddCargo = async () => {
		if (!selectedCargoName) return;
		try {
			const resp = await funcionogramaService.post({
				organizational_structure_id: id,
				name: selectedCargoName,
			});
			setCargos((oldCargos) => [
				...oldCargos,
				{ label: selectedCargoName, value: resp?.id },
			]);
			setSelectedCargo({ label: selectedCargoName, value: resp?.id });
			setShowModalAddCargo(false); // Close the modal
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Cargo adicionado com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			const message =
				error?.response?.data?.message || "Algo deu errado!";
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	const handleRemoveCargo = async () => {
		try {
			await funcionogramaService.del(selectedCargo.value);
			getCargos();
			setSelectedCargoName("");
			setSelectedCargo();
			setShowModalDelete(false);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Cargo removido com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			const message =
				error?.response?.data?.message || "Algo deu errado!";
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	const handleSaveAtividades = async () => {
		try {
			for (const atividade of atividades) {
				if (atividade.isNew) {
					await funcionogramaService.postAtividade({
						organizational_structure_jobs_id: selectedCargo?.value,
						name: atividade.value,
					});
				} else {
					await funcionogramaService.putAtividade({
						id: atividade.id,
						name: atividade.value,
					});
				}
			}
			getAtividades(selectedCargo?.value);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Atividades salvas com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			const message =
				error?.response?.data?.message || "Algo deu errado!";
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	const handleDeleteAtividade = async () => {
		try {
			await funcionogramaService.delAtividade(atividadeIdDelete);
			setAtividadeIdDelete();
			getAtividades(selectedCargo?.value);
			setShowModalDeleteAtividade(false);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Atividade excluída com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			const message =
				error?.response?.data?.message || "Algo deu errado!";
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	return (
		<>
			<DeleteModal
				show={showModalDelete}
				onCloseClick={() => setShowModalDelete(!showModalDelete)}
				onDeleteClick={handleRemoveCargo}
			/>
			<DeleteModal
				show={showModalDeleteAtividade}
				onCloseClick={() =>
					setShowModalDeleteAtividade(!showModalDeleteAtividade)
				}
				onDeleteClick={handleDeleteAtividade}
			/>

			<AddCargoModal
				show={showModalAddCargo}
				onCloseClick={() => setShowModalAddCargo(false)}
				onAddClick={handleAddCargo}
				onNameChange={(e) => setSelectedCargoName(e.target.value)}
			/>

			<Row className="py-1">
				<div className="d-flex justify-content-between align-items-center w-100">
					<div className="d-flex gap-2"></div>
					<div className="d-flex gap-2">
						<Button
							onClick={() => {
								const baseUrl =
									window.location.origin +
									window.location.pathname;
								let url = `${baseUrl}#/print/pdf/estrutura?id=${id}`;

								if (selectedCargo?.value) {
									url += `&cargo_id=${selectedCargo.value}`;
								}

								window.open(url, "_blank");
							}}
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
								marginLeft: 10,
							}}
						>
							<Printer size={16} /> Imprimir
						</Button>
					</div>
				</div>
			</Row>

			<Col md="12">
				<Row className="align-items-center">
					<Col md="6">
						<FormGroup className="mb-3">
							<Label>Cargo</Label>
							<Select
								styles={{
									...customStyles,
									menu: (base) => ({
										...base,
										zIndex: 9999999,
									}),
								}}
								placeholder="Selecione o Cargo"
								onChange={handleChangeCargo}
								value={
									cargos.find(
										(option) =>
											option.value ===
											selectedCargo?.value,
									) || null
								}
								options={cargos}
							/>
						</FormGroup>
					</Col>
					<Col md="6" style={{ marginTop: 10, display: "flex" }}>
						<Tooltip title="Adicionar" placement="top">
							<IconButton
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									border: "1px solid black",
									borderRadius: 4,
								}}
								onClick={() => setShowModalAddCargo(true)}
							>
								<Plus size={16} />
							</IconButton>
						</Tooltip>
						{/* </Col> */}
						{/* <Col md="3"> */}
						{selectedCargo && (
							<Tooltip title="Excluir" placement="top">
								<IconButton
									disabled={!selectedCargo}
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginLeft: 6,
										border: "1px solid black",
										borderRadius: 4,
									}}
									onClick={() =>
										setShowModalDelete(!showModalDelete)
									}
								>
									<Trash size={16} />
								</IconButton>
							</Tooltip>
						)}
					</Col>
				</Row>

				{selectedCargo && (
					<>
						<Row className="py-1">
							<Table className="table table-hover table-bordered table-nowrap mb-0">
								<thead className="table-light">
									<tr>
										{columns.map((column, index) => (
											<th
												key={index}
												style={column.style}
												className="text-center align-middle"
											>
												{column.name}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{atividades.map((atividade, index) => (
										<tr key={atividade.id}>
											<td className="text-center align-middle">
												{index + 1}
											</td>
											<td className="text-center align-middle">
												<Input
													type="text"
													value={atividade.value}
													onChange={(e) =>
														handleChangeAtividade(
															atividade.id,
															e.target.value,
														)
													}
												/>
											</td>
											<td className="text-center align-middle">
												<Tooltip
													title="Excluir"
													placement="top"
												>
													<IconButton
														style={{
															background: "none",
															border: "none",
															padding: 0,
															cursor: "pointer",
															color: "black",
														}}
														onClick={() => {
															setAtividadeIdDelete(
																atividade.id,
															);
															setShowModalDeleteAtividade(
																!showModalDeleteAtividade,
															);
														}}
													>
														<Trash size={16} />
													</IconButton>
												</Tooltip>
											</td>
										</tr>
									))}
									{/* Nova Atividade Diretamente na Tabela */}
									<tr>
										<td className="text-center align-middle">
											{atividades.length + 1}
										</td>
										<td className="text-center align-middle">
											<Input
												type="text"
												placeholder="Insira nova atividade"
												value={novaAtividade}
												onChange={(e) =>
													setNovaAtividade(
														e.target.value,
													)
												}
											/>
										</td>
										<td className="text-center align-middle">
											<Tooltip
												title="Inserir"
												placement="top"
											>
												<IconButton
													onClick={handleAddAtividade}
													style={{
														background: "none",
														border: "none",
														padding: 0,
														cursor: "pointer",
														color: "black",
													}}
												>
													<Plus size={16} />
												</IconButton>
											</Tooltip>
										</td>
									</tr>
								</tbody>
							</Table>
						</Row>
						<Row className="py-1">
							<Col className="d-flex justify-content-end">
								<Button
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
									}}
									onClick={onCloseClick}
								>
									<SignOut size={16} /> Fechar
								</Button>
								<Button
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										marginLeft: 10,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
									}}
									onClick={handleSaveAtividades}
								>
									<FloppyDisk size={16} /> Salvar
								</Button>
							</Col>
						</Row>
					</>
				)}
			</Col>
		</>
	);
};

Functionogram.propTypes = {
	onCloseClick: PropTypes.func,
	data: PropTypes.object,
};

export default Functionogram;
